import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import {
  BusinessReview,
  useCreateBusinessReviewMutation,
} from "store/apis/businessReview";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (review: BusinessReview) => void;
};

const buildInitialValues = () => ({
  start_date: dayjs()
    .startOf("quarter")
    .subtract(1, "quarter")
    .format(YYYY_MM_DD),
  end_date: dayjs().startOf("quarter").subtract(1, "day").format(YYYY_MM_DD),
  comp_start_date: dayjs()
    .startOf("quarter")
    .subtract(2, "quarter")
    .format(YYYY_MM_DD),
  comp_end_date: dayjs()
    .startOf("quarter")
    .subtract(1, "quarter")
    .subtract(1, "day")
    .format(YYYY_MM_DD),
  theme: "light" as const,
});

export const GenerateReviewModal = ({ isOpen, onClose, onCreated }: Props) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [includeComparison, setIncludeComparison] = useState(true);

  const [createReview, { isLoading }] = useCreateBusinessReviewMutation();

  const handleGenerate = async (
    values: ReturnType<typeof buildInitialValues>
  ) => {
    try {
      const result = await createReview({
        entityId: entityId!,
        start_date: values.start_date,
        end_date: values.end_date,
        comparison_start_date: includeComparison
          ? values.comp_start_date
          : null,
        comparison_end_date: includeComparison ? values.comp_end_date : null,
        theme: values.theme,
      }).unwrap();
      successToast({ message: "Business Review generated successfully!" });
      onCreated(result);
      onClose();
    } catch {
      alertToast({ message: "Failed to generate Business Review." });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Generate Financial Review</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <FormikForm
          initialValues={buildInitialValues()}
          onSubmit={handleGenerate}
        >
          {({ values, isSubmitting }) => (
            <>
              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-5">
                  <div>
                    <p className="t-text-subtitle-sm t-text-text-60 t-mb-3">
                      Review Period
                    </p>
                    <div className="t-flex t-gap-3">
                      <div className="t-flex-1">
                        <DateField
                          name="start_date"
                          label="Start Date"
                          placeholder="Start date"
                          maxDate={
                            values.end_date
                              ? new Date(values.end_date)
                              : undefined
                          }
                        />
                      </div>
                      <div className="t-flex-1">
                        <DateField
                          name="end_date"
                          label="End Date"
                          placeholder="End date"
                          minDate={
                            values.start_date
                              ? new Date(values.start_date)
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <Checkbox
                    label="Include comparison period"
                    checked={includeComparison}
                    onChange={(e) => setIncludeComparison(e.target.checked)}
                  />

                  {includeComparison && (
                    <div>
                      <p className="t-text-subtitle-sm t-text-text-60 t-mb-3">
                        Comparison Period
                      </p>
                      <div className="t-flex t-gap-3">
                        <div className="t-flex-1">
                          <DateField
                            name="comp_start_date"
                            label="Start Date"
                            placeholder="Comparison start"
                            maxDate={
                              values.comp_end_date
                                ? new Date(values.comp_end_date)
                                : undefined
                            }
                          />
                        </div>
                        <div className="t-flex-1">
                          <DateField
                            name="comp_end_date"
                            label="End Date"
                            placeholder="Comparison end"
                            minDate={
                              values.comp_start_date
                                ? new Date(values.comp_start_date)
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={onClose} customType="ghost" type="button">
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isLoading || isSubmitting}
                  disabled={!values.start_date || !values.end_date}
                >
                  Generate Financial Review
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
